import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { QrCode, Loader2, Check, Copy, RefreshCw } from "lucide-react";
import { papi } from "../../lib/portalApi";
import { brl, formatDate, formatErr } from "../../lib/api";
import { setPortalBusy } from "../../lib/portalPwa";

const PURPLE = "#4C2380";
const LILAC = "#D9C4F0";

// PIX individual (bu + installmentId) ou agrupado (group = {due_date, key}). Valores sempre do servidor.
export function PixModal({ open, onClose, bu, installmentId, group }) {
  const [state, setState] = useState("loading");
  const [charge, setCharge] = useState(null);
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setState("loading"); setMsg("");
    try {
      const { data } = group
        ? await papi.post("/portal/payment-center/pix", { due_date: group.due_date, group_key: group.key })
        : await papi.post(`/portal/installments/${bu}/${installmentId}/pix`);
      setCharge(data); setState("ok");
    } catch (e) {
      // 5xx/erro de rede: mensagem amigavel (o proxy pode trocar o corpo por HTML em ingles).
      const st = e?.response?.status || 0;
      setMsg((st && st < 500 && formatErr(e?.response?.data?.detail)) || "Não foi possível gerar o PIX agora. Tente novamente em instantes.");
      setState("error");
    }
  }, [bu, installmentId, group]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { if (open) { setPortalBusy(true); return () => setPortalBusy(false); } }, [open]);
  if (!open) return null;

  const refresh = async () => {
    const id = group ? charge?.group_charge_id : charge?.charge_id;
    if (!id) return;
    try {
      const { data } = group
        ? await papi.post(`/portal/payment-center/pix/${id}/refresh`)
        : await papi.post(`/portal/pix/${bu}/${id}/refresh`);
      setCharge(data);
      if (group && data.settlement_state === "settled") setMsg(`Pagamento confirmado! ${data.count} parcelas quitadas.`);
      else if (group && data.mp_status === "approved") setMsg("Pagamento recebido. Em conciliação.");
      else if (data.status === "paid" || data.status === "pago") setMsg("Pagamento confirmado!");
    } catch { /* silencioso */ }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(charge.copy_paste || ""); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* */ }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose} data-testid="portal-pix-modal">
      <div className="w-full max-w-md rounded-t-3xl bg-white p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:rounded-3xl sm:pb-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center gap-2"><QrCode size={20} style={{ color: PURPLE }} />
          <h3 className="text-lg font-bold" style={{ color: PURPLE, fontFamily: "inherit" }}>Pagar com PIX</h3></div>
        {state === "loading" && <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin" style={{ color: PURPLE }} /></div>}
        {state === "error" && (
          <div className="rounded-2xl bg-amber-50 px-4 py-4 text-sm text-amber-800" data-testid="portal-pix-error">{msg}</div>
        )}
        {state === "ok" && charge && (
          <div className="space-y-3">
            <div className="text-center text-sm text-gray-500" data-testid="portal-pix-label">
              {group ? `${charge.count} parcelas · vencimento ${formatDate(charge.due_date)}` : "Valor atualizado"}
            </div>
            <div className="text-center text-3xl font-extrabold tabular-nums" style={{ color: PURPLE }} data-testid="portal-pix-amount">{brl(charge.amount)}</div>
            {charge.qr_code_data_url && (
              <img src={charge.qr_code_data_url} alt="QR Code PIX" className="mx-auto h-56 w-56 rounded-2xl border border-gray-100" data-testid="portal-pix-qr" />
            )}
            {charge.copy_paste && (
              <button onClick={copy} data-testid="portal-pix-copy"
                className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 py-3 text-sm font-bold"
                style={{ borderColor: LILAC, color: PURPLE }}>
                {copied ? <><Check size={16} /> Código copiado!</> : <><Copy size={16} /> Copiar código PIX</>}
              </button>
            )}
            {charge.expires_at && <div className="text-center text-[11px] text-gray-400">Válido até {formatDate(charge.expires_at)} (23:59)</div>}
            {msg && <div className="rounded-xl bg-green-50 px-3 py-2 text-center text-sm font-semibold text-green-700">{msg}</div>}
            <button onClick={refresh} data-testid="portal-pix-refresh"
              className="flex w-full items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold text-white" style={{ background: PURPLE }}>
              <RefreshCw size={16} /> Já paguei / Atualizar
            </button>
          </div>
        )}
        <button onClick={onClose} className="mt-4 w-full rounded-2xl py-2.5 text-sm font-semibold text-gray-500" data-testid="portal-pix-close">Fechar</button>
      </div>
    </div>,
    document.body
  );
}
